import React from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Link } from 'react-router-dom';
import GlassmorphismTrustHero from '../components/ui/glassmorphism-trust-hero';
import ScrollToTopOnMount from '../components/ScrollToTopOnMount';
import { Award, Users, Target, TrendingUp, ArrowRight, Mail, Settings, CheckCircle, Lightbulb, Heart } from 'lucide-react';

const AboutPage = () => {
  // Service categories for the scrolling bar
  const categories = [
    "Laser Cutting",
    "CNC Bending",
    "Metal Fabrication",
    "Precision Engineering",
    "Custom Solutions"
  ];

  // Counter Component for animated numbers
  const AnimatedCounter = ({ value, suffix = '' }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, Math.round);
    const [displayValue, setDisplayValue] = React.useState(0);

    React.useEffect(() => {
      const unsubscribe = rounded.on('change', (latest) => {
        setDisplayValue(latest);
      });

      const controls = animate(count, value, {
        duration: 2,
        ease: "easeOut"
      });

      return () => {
        unsubscribe();
        controls.stop();
      };
    }, [count, rounded, value]);

    return <>{displayValue}{suffix}</>;
  };

  return (
    <>
      <ScrollToTopOnMount />
      <GlassmorphismTrustHero />
      
      {/* Hero Bottom Section - Agency Style */}
      <section className="relative bg-gray-50 overflow-hidden">
        {/* Scrolling Categories Bar */}
        <div className="bg-white border-b border-gray-100 py-6 overflow-hidden">
          <div className="flex items-center gap-12 animate-marquee-slow whitespace-nowrap">
            {[...categories, ...categories, ...categories].map((category, index) => (
              <div key={index} className="flex items-center gap-4">
                <span className="w-3 h-3 rounded-full bg-primary"></span>
                <span className="text-lg md:text-xl font-semibold text-steel uppercase tracking-wider">{category}</span>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes marquee-slow {
            from { transform: translateX(0); }
            to { transform: translateX(-33.33%); }
          }
          .animate-marquee-slow {
            animation: marquee-slow 20s linear infinite;
          }
        `}</style>

        <div className="max-w-7xl mx-auto px-6 py-28 lg:py-36">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-28">
            {/* Left - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-steel leading-tight mb-8">
                Manufacturing challenges<br />
                <span className="text-primary">and their best solutions</span>
              </h2>
              <p className="text-steel-lighter text-xl leading-relaxed mb-10">
                Crafting compelling metal experiences that captivate industries and drive meaningful connections. 
                Our precision approach combines innovation, reliability and a builder mind who are equipped and 
                focused on growing our clients' manufacturing projects.
              </p>
              <div className="flex items-center gap-3 sm:gap-6">
                <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-primary/10 rounded-full">
                  <span className="text-primary font-bold text-base sm:text-xl">3+</span>
                  <span className="text-steel-lighter text-sm sm:text-base">Years Experience</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-secondary/10 rounded-full">
                  <span className="text-secondary font-bold text-base sm:text-xl">1000+</span>
                  <span className="text-steel-lighter text-sm sm:text-base">Projects</span>
                </div>
              </div>
            </motion.div>

            {/* Right - Image Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-3 sm:gap-6"
            >
              <div className="space-y-3 sm:space-y-6">
                <img
                  src="https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet2.png"
                  alt="Precision Sheet Laser Cutting - Alpha Laser Technologies"
                  className="rounded-xl sm:rounded-2xl object-cover h-36 sm:h-56 w-full shadow-lg"
                />
                <div className="bg-primary rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white">
                  <p className="text-[10px] sm:text-sm font-medium uppercase tracking-wider mb-1 sm:mb-2">ISO Certified</p>
                  <p className="text-xl sm:text-3xl font-bold">Quality First</p>
                  <p className="text-[10px] sm:text-sm font-medium mt-1 sm:mt-3 text-white/80">GSTIN: 33ESZPR8850J1ZM</p>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-6 pt-5 sm:pt-10">
                <div className="bg-steel rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white">
                  <p className="text-[10px] sm:text-sm font-medium uppercase tracking-wider mb-1 sm:mb-2">Precision</p>
                  <p className="text-xl sm:text-3xl font-bold">±0.1mm Accuracy</p>
                </div>
                <img
                  src="https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png"
                  alt="CNC Bending Services - Alpha Laser Technologies"
                  className="rounded-xl sm:rounded-2xl object-cover h-36 sm:h-56 w-full shadow-lg"
                />
              </div>
            </motion.div>
          </div>

          {/* Services Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-steel mb-4">
              Why Choose <span className="text-primary">Alpha Laser</span>
            </h3>
            <p className="text-steel-lighter text-lg max-w-2xl">
              We combine cutting-edge technology with decades of expertise to deliver manufacturing excellence that drives your success.
            </p>
          </motion.div>

          {/* About Cards - Mission, Vision, Commitment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-primary/30 hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-all duration-500" />
              
              <div className="relative w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <Target className="w-8 h-8 text-white" />
              </div>
              
              <h4 className="text-2xl font-bold text-steel mb-4 group-hover:text-primary transition-colors duration-300">
                Our Mission
              </h4>
              
              <p className="text-steel-lighter leading-relaxed mb-6">
                To deliver precision metal fabrication solutions that exceed industry standards, empowering businesses across South India with reliable, high-quality manufacturing services.
              </p>
              
              <ul className="space-y-3">
                {["Client-centric approach", "Continuous improvement", "On-time delivery"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-steel">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-primary group-hover:w-full transition-all duration-500" />
            </motion.div>

            {/* Vision Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-secondary/40 hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/5 rounded-full blur-3xl group-hover:bg-secondary/10 transition-all duration-500" />
              
              <div className="relative w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              
              <h4 className="text-2xl font-bold text-steel mb-4 group-hover:text-secondary transition-colors duration-300">
                Our Vision
              </h4>
              
              <p className="text-steel-lighter leading-relaxed mb-6">
                To become India's most trusted name in precision manufacturing by setting new benchmarks in quality, innovation, and customer satisfaction in the metal fabrication industry.
              </p>
              
              <ul className="space-y-3">
                {["Industry leadership", "Technology innovation", "Sustainable growth"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-steel">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-secondary group-hover:w-full transition-all duration-500" />
            </motion.div>

            {/* Commitment Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-green-300 hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full blur-3xl group-hover:bg-green-500/10 transition-all duration-500" />
              
              <div className="relative w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <Heart className="w-8 h-8 text-white" />
              </div>
              
              <h4 className="text-2xl font-bold text-steel mb-4 group-hover:text-green-600 transition-colors duration-300">
                Our Commitment
              </h4>
              
              <p className="text-steel-lighter leading-relaxed mb-6">
                We are committed to building lasting partnerships through transparency, reliability, and unwavering dedication to quality in every project we undertake.
              </p>
              
              <ul className="space-y-3">
                {["ISO 9001:2015 certified", "100% quality assurance", "Long-term partnerships"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-steel">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-green-500 group-hover:w-full transition-all duration-500" />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="relative overflow-hidden">
        {/* Top Wave SVG inside section */}
        <div className="absolute top-0 left-0 w-full z-10">
          <svg className="w-full block" viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ height: '120px' }}>
            <path fill="#f9fafb" d="M0,0 C480,100 960,20 1440,80 L1440,0 L0,0 Z"></path>
          </svg>
        </div>
        
        {/* Fixed Background Image */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1600&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed'
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-gray-100/90 to-slate-200/95"></div>
        </div>
        
        {/* Background Decorations */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl z-[1]"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl z-[1]"></div>
        
        <div className="max-w-7xl mx-auto relative z-10 py-32 px-6 pt-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <p className="text-sm font-medium text-primary uppercase tracking-[0.2em] mb-4">Our Values</p>
            <h2 className="text-5xl md:text-6xl font-bold text-steel mb-6 tracking-tight">
              What Drives <span className="text-primary">Us</span> <span className="text-secondary">Forward</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-steel-lighter max-w-3xl mx-auto">
              Our core values shape everything we do, from client relationships to manufacturing excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: "Excellence",
                description: "Committed to delivering the highest quality in every project, exceeding industry standards",
                color: "from-[rgb(15,14,151)] to-cyan-500"
              },
              {
                icon: Users,
                title: "Client-Focused",
                description: "Building lasting partnerships through exceptional service and consistent communication",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: Target,
                title: "Precision",
                description: "Utilizing cutting-edge technology to achieve accuracy down to the micron level",
                color: "from-orange-500 to-red-500"
              },
              {
                icon: TrendingUp,
                title: "Innovation",
                description: "Continuously evolving our processes and adopting the latest manufacturing technologies",
                color: "from-green-500 to-emerald-500"
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-steel mb-4 group-hover:text-primary transition-colors duration-300">
                    {value.title}
                  </h3>
                  
                  <p className="text-steel-lighter leading-relaxed">
                    {value.description}
                  </p>
                  
                  <div className={`w-0 h-1 bg-gradient-to-r ${value.color} mt-6 group-hover:w-full transition-all duration-500 rounded-full`}></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { number: 3, suffix: "+", label: "Years Experience" },
              { number: 500, suffix: "+", label: "Happy Clients" },
              { number: 1000, suffix: "+", label: "Projects Completed" },
              { number: 95, suffix: "%", label: "Satisfaction Rate" }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1, type: "spring" }}
                  className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary mb-2"
                >
                  <AnimatedCounter value={stat.number} suffix={stat.suffix} />
                </motion.div>
                <p className="text-steel-lighter font-medium uppercase tracking-wider text-sm">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-24"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact CTA Card */}
              <Link to="/contact" className="group">
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-2xl transition-all duration-300">
                  {/* Background Image */}
                  <img
                    src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=800&q=80"
                    alt="Contact Background"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary-dark/90 group-hover:from-primary/95 group-hover:to-primary-dark/95 transition-all duration-300" />
                  
                  {/* Content */}
                  <div className="relative z-10 p-8 md:p-10 h-72 flex flex-col justify-between">
                    <div>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        Ready to Start Your Project?
                      </h3>
                      <p className="text-white/90 text-base leading-relaxed">
                        Get in touch with our team for a free consultation and quote.
                      </p>
                    </div>
                    
                    <div className="inline-flex items-center px-5 py-2.5 rounded-full backdrop-blur-md bg-white/20 border border-white/30 text-white font-semibold gap-2 group-hover:gap-3 group-hover:bg-white/30 transition-all duration-300 shadow-lg w-fit">
                      <span>Contact Us</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Services CTA Card */}
              <Link to="/services" className="group">
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-2xl transition-all duration-300">
                  {/* Background Image */}
                  <img
                    src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&q=80"
                    alt="Services Background"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-steel/90 to-steel-dark/90 group-hover:from-steel/95 group-hover:to-steel-dark/95 transition-all duration-300" />
                  
                  {/* Content */}
                  <div className="relative z-10 p-8 md:p-10 h-72 flex flex-col justify-between">
                    <div>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Settings className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        Explore Our Services
                      </h3>
                      <p className="text-white/90 text-base leading-relaxed">
                        Discover our full range of precision manufacturing services.
                      </p>
                    </div>
                    
                    <div className="inline-flex items-center px-5 py-2.5 rounded-full backdrop-blur-md bg-white/20 border border-white/30 text-white font-semibold gap-2 group-hover:gap-3 group-hover:bg-white/30 transition-all duration-300 shadow-lg w-fit">
                      <span>View Services</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
